
import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { adminAPI } from '../services/api';

/* ============================================================================
   PromotionActivity.js
   Promotion Management — campaigns + slides + engagement stats:
     GET  /api/admin/promo-campaigns          → list all campaigns
     GET  /api/admin/promo-campaigns/stats    → impressions / clicks / CTR
     PUT  /api/admin/promo-campaigns          → upsert (create / update)
   Mobile apps POST /api/app/promo-modal/events for impression, click, dismiss.
   ========================================================================== */

const ENVIRONMENTS = ['production', 'staging'];
const PLATFORMS    = ['all', 'ios', 'android'];
const CTA_TYPES    = ['tab', 'url', 'dismiss'];

const TABS = [
  { key: 'campaigns', label: 'Campaigns' },
  { key: 'create',    label: 'Create / Edit' },
];

const EMPTY_FILTERS = { env: 'all', platform: 'all', active: 'all' };

let _ctr = 1000;
const uid = (p) => `${p}_${Date.now()}_${_ctr++}`;

/* ---------------------------------------------------------------------------
   Helpers
   ------------------------------------------------------------------------- */
const cx = (...p) => p.filter(Boolean).join(' ');

const fmtDate = (v) => {
  if (!v) return '—';
  const d = new Date(v);
  return isNaN(d) ? '—' : d.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
};

const deriveStatus = (c) => {
  const now = new Date();
  const start = c.startAt ? new Date(c.startAt) : null;
  const end   = c.endAt   ? new Date(c.endAt)   : null;
  if (!c.active) return 'inactive';
  if (start && start > now) return 'scheduled';
  if (end   && end   < now) return 'expired';
  return 'active';
};

const statusCls = (s) => ({
  active:    'badge badge-success',
  inactive:  'badge badge-neutral',
  scheduled: 'badge badge-info',
  expired:   'badge badge-danger',
}[s] || 'badge badge-neutral');

const platformLabel = (p) => ({ ios: 'iOS', android: 'Android', all: 'All platforms' }[p] || p);

const fmtNum = (n) => (n == null || Number.isNaN(Number(n)) ? '—' : Number(n).toLocaleString());

const fmtCtr = (stats) => {
  if (!stats?.impressions?.total) return '—';
  return `${((stats.clicks?.total || 0) / stats.impressions.total * 100).toFixed(1)}%`;
};

const emptyCampaignStats = () => ({
  impressions: { total: 0, uniqueUsers: 0 },
  clicks: { total: 0, uniqueUsers: 0 },
  dismisses: { total: 0, uniqueUsers: 0 },
  ctr: 0,
  byPlatform: {
    ios: { impressions: 0, clicks: 0, dismisses: 0 },
    android: { impressions: 0, clicks: 0, dismisses: 0 },
  },
  bySlide: {},
});

/* ---------------------------------------------------------------------------
   Empty factories
   ------------------------------------------------------------------------- */
const emptySlide = () => ({
  id:          uid('slide'),
  type:        'image',
  mediaUrl:    '',
  title:       '',
  description: '',
  ctaLabel:    'Learn more',
  ctaAction:   { type: 'dismiss', value: '' },
});

const emptyCampaign = () => ({
  campaignId:           '',
  env:                  'production',
  platform:             'all',
  active:               false,
  priority:             0,
  maxImpressionsPerDay: 2,
  startAt:              '',
  endAt:                '',
  slides:               [emptySlide()],
});

/* ============================================================================
   Component
   ========================================================================== */
export default function PromotionActivity() {
  const [activeTab, setActiveTab]         = useState('campaigns');
  const [loading, setLoading]             = useState(false);
  const [campaignsLoading, setCampaignsLoading] = useState(true);
  const [submitting, setSubmitting]       = useState(false);

  const [campaigns, setCampaigns]         = useState([]);
  const [campaignStats, setCampaignStats] = useState({});
  const [statsLoading, setStatsLoading]   = useState(true);
  const [statsDays, setStatsDays]         = useState('all');
  const [search, setSearch]               = useState('');
  const [filters, setFilters]             = useState(EMPTY_FILTERS);
  const [envFilter, setEnvFilter]         = useState('');   // for GET ?env=

  const [formData, setFormData]           = useState(emptyCampaign());
  const [slides, setSlides]               = useState(formData.slides);
  const [editingId, setEditingId]         = useState(null); // campaignId being edited
  const [previewIdx, setPreviewIdx]       = useState(0);
  const [uploadingSlides, setUploadingSlides] = useState({});

  const [modal, setModal]                 = useState({ type: null, payload: null });
  const [toast, setToast]                 = useState(null);
  const toastRef                          = useRef(null);
  const slideFileInputRefs                = useRef({});

  /* -------------------------------------------------------------------------
     Toast
     ----------------------------------------------------------------------- */
  const showToast = useCallback((message, kind = 'success') => {
    setToast({ message, kind });
    if (toastRef.current) clearTimeout(toastRef.current);
    toastRef.current = setTimeout(() => setToast(null), 3200);
  }, []);

  useEffect(() => () => toastRef.current && clearTimeout(toastRef.current), []);

  /* -------------------------------------------------------------------------
     API calls
     ----------------------------------------------------------------------- */
  const loadCampaignStats = useCallback(async (env = '', days = statsDays) => {
    setStatsLoading(true);
    try {
      const params = {};
      if (env) params.env = env;
      if (days !== 'all' && days) params.days = Number(days);
      const res = await adminAPI.getPromoCampaignStats(params);
      setCampaignStats(res?.data?.stats ?? {});
    } catch (err) {
      console.error('loadCampaignStats failed:', err);
      setCampaignStats({});
    } finally {
      setStatsLoading(false);
    }
  }, [statsDays]);

  const loadCampaigns = useCallback(async (env = '') => {
    setCampaignsLoading(true);
    try {
      const params = env ? { env } : {};
      const res    = await adminAPI.getPromoCampaigns(params);
      const list   = res?.data?.campaigns ?? res?.data ?? res ?? [];
      setCampaigns(Array.isArray(list) ? list : []);
    } catch (err) {
      console.error('loadCampaigns failed:', err);
      showToast('Failed to load campaigns — ' + (err?.message || 'unknown error'), 'error');
      setCampaigns([]);
    } finally {
      setCampaignsLoading(false);
    }
  }, [showToast]);

  const reloadAll = useCallback(async (env = envFilter) => {
    await Promise.all([loadCampaigns(env), loadCampaignStats(env)]);
  }, [envFilter, loadCampaigns, loadCampaignStats]);

  useEffect(() => { reloadAll(envFilter); }, [envFilter, statsDays]); // eslint-disable-line

  const upsertCampaign = useCallback(async (payload) => {
    // PUT /api/admin/promo-campaigns  — single endpoint for create + update
    const res     = await adminAPI.upsertPromoCampaign(payload);
    const updated = res?.data?.campaign ?? res?.data ?? payload;
    return updated;
  }, []);

  /* -------------------------------------------------------------------------
     Form helpers
     ----------------------------------------------------------------------- */
  const resetForm = useCallback(() => {
    const empty = emptyCampaign();
    setFormData(empty);
    setSlides(empty.slides);
    setEditingId(null);
    setPreviewIdx(0);
  }, []);

  const loadIntoForm = useCallback((campaign) => {
    setFormData({
      campaignId:           campaign.campaignId,
      env:                  campaign.env       ?? 'production',
      platform:             campaign.platform  ?? 'all',
      active:               campaign.active    ?? false,
      priority:             campaign.priority  ?? 0,
      maxImpressionsPerDay: campaign.maxImpressionsPerDay ?? 2,
      startAt:              campaign.startAt   ? campaign.startAt.slice(0, 10) : '',
      endAt:                campaign.endAt     ? campaign.endAt.slice(0, 10)   : '',
    });
    const sl = campaign.slides?.length ? campaign.slides : [emptySlide()];
    setSlides(sl);
    setEditingId(campaign.campaignId);
    setPreviewIdx(0);
    setActiveTab('create');
  }, []);

  const validate = useCallback(() => {
    if (!formData.campaignId.trim()) return 'Campaign ID is required.';
    if (!/^[a-z0-9_]+$/i.test(formData.campaignId.trim())) return 'Campaign ID: letters, numbers, underscores only.';
    if (formData.startAt && formData.endAt && formData.startAt > formData.endAt) return 'Start date must be before end date.';
    if (!slides.length) return 'Add at least one slide.';
    for (const s of slides) {
      if (!s.mediaUrl.trim()) return 'Every slide needs an uploaded image.';
      if (!s.title.trim())    return 'Every slide needs a title.';
      if (s.ctaAction?.type !== 'dismiss' && !s.ctaAction?.value?.trim()) return 'Every non-dismiss CTA needs a value.';
    }
    return null;
  }, [formData, slides]);

  /* -------------------------------------------------------------------------
     Submit — upsert
     ----------------------------------------------------------------------- */
  const handleSubmit = useCallback(async (e) => {
    e.preventDefault();
    const err = validate();
    if (err) { showToast(err, 'error'); return; }
    setSubmitting(true);
    const payload = {
      ...formData,
      campaignId: formData.campaignId.trim(),
      slides,
    };
    try {
      const saved = await upsertCampaign(payload);
      const derived = { ...saved, status: deriveStatus(saved) };
      setCampaigns((prev) => {
        const idx = prev.findIndex((c) => c.campaignId === derived.campaignId);
        return idx >= 0
          ? prev.map((c) => c.campaignId === derived.campaignId ? derived : c)
          : [derived, ...prev];
      });
      showToast(editingId ? 'Campaign updated.' : 'Campaign created.');
      resetForm();
      setActiveTab('campaigns');
    } catch (err) {
      console.error('upsert failed:', err);
      showToast('Save failed — ' + (err?.message || 'unknown error'), 'error');
    } finally {
      setSubmitting(false);
    }
  }, [formData, slides, editingId, validate, upsertCampaign, resetForm, showToast]);

  /* -------------------------------------------------------------------------
     Toggle active — upsert with flipped active flag
     ----------------------------------------------------------------------- */
  const handleToggle = useCallback(async (campaign) => {
    const next = { ...campaign, active: !campaign.active };
    try {
      const saved = await upsertCampaign(next);
      const derived = { ...saved, status: deriveStatus(saved) };
      setCampaigns((prev) => prev.map((c) => c.campaignId === derived.campaignId ? derived : c));
      showToast(next.active ? 'Campaign enabled.' : 'Campaign disabled.');
    } catch (err) {
      showToast('Toggle failed — ' + (err?.message || 'unknown error'), 'error');
    }
    setModal({ type: null, payload: null });
  }, [upsertCampaign, showToast]);

  /* -------------------------------------------------------------------------
     Slide editing
     ----------------------------------------------------------------------- */
  const updateSlide = useCallback((id, field, value) =>
    setSlides((p) => p.map((s) => s.id === id ? { ...s, [field]: value } : s)), []);

  const updateSlideCtaAction = useCallback((id, field, value) =>
    setSlides((p) => p.map((s) => s.id === id ? { ...s, ctaAction: { ...s.ctaAction, [field]: value } } : s)), []);

  const addSlide    = useCallback(() => setSlides((p) => [...p, emptySlide()]), []);
  const removeSlide = useCallback((id) => {
    setSlides((p) => p.length > 1 ? p.filter((s) => s.id !== id) : p);
    setPreviewIdx((i) => Math.max(0, i - 1));
  }, []);
  const moveSlide   = useCallback((id, dir) => setSlides((p) => {
    const i = p.findIndex((s) => s.id === id);
    const j = i + dir;
    if (i < 0 || j < 0 || j >= p.length) return p;
    const n = [...p]; [n[i], n[j]] = [n[j], n[i]]; return n;
  }), []);

  const triggerSlideUpload = useCallback((slideId) => {
    slideFileInputRefs.current[slideId]?.click();
  }, []);

  const handleSlideMediaUpload = useCallback(async (slideId, file) => {
    if (!file) return;

    const allowedTypes = ['image/jpeg', 'image/png', 'image/webp', 'image/gif'];
    if (!allowedTypes.includes(file.type)) {
      showToast('Please upload a JPEG, PNG, WebP, or GIF image.', 'error');
      return;
    }
    if (file.size > 10 * 1024 * 1024) {
      showToast('Image must be 10MB or smaller.', 'error');
      return;
    }

    setUploadingSlides((prev) => ({ ...prev, [slideId]: true }));
    try {
      const res = await adminAPI.uploadPromoSlideMedia(file);
      const mediaUrl = res?.data?.mediaUrl;
      if (!mediaUrl) throw new Error('Upload did not return a media URL');
      updateSlide(slideId, 'mediaUrl', mediaUrl);
      showToast('Image uploaded.');
    } catch (err) {
      const message =
        err?.response?.data?.error ||
        err?.message ||
        'Upload failed';
      showToast(message, 'error');
    } finally {
      setUploadingSlides((prev) => {
        const next = { ...prev };
        delete next[slideId];
        return next;
      });
    }
  }, [showToast, updateSlide]);

  /* -------------------------------------------------------------------------
     Filtering
     ----------------------------------------------------------------------- */
  const filtered = useMemo(() => {
    const term = search.trim().toLowerCase();
    return campaigns
      .map((c) => ({ ...c, status: deriveStatus(c) }))
      .filter((c) => {
        if (term && !c.campaignId.toLowerCase().includes(term)) return false;
        if (filters.env      !== 'all' && c.env      !== filters.env)             return false;
        if (filters.platform !== 'all' && c.platform !== filters.platform)        return false;
        if (filters.active   !== 'all' && String(c.active) !== filters.active)    return false;
        return true;
      });
  }, [campaigns, search, filters]);

  /* -------------------------------------------------------------------------
     Export CSV
     ----------------------------------------------------------------------- */
  const handleExport = useCallback(() => {
    const rows = campaigns.map((c) => ({
      campaignId: c.campaignId, env: c.env, platform: c.platform,
      active: c.active, priority: c.priority,
      maxImpressionsPerDay: c.maxImpressionsPerDay,
      startAt: c.startAt, endAt: c.endAt,
      slides: c.slides?.length ?? 0,
    }));
    const headers = Object.keys(rows[0] || {});
    const csv = [headers.join(','), ...rows.map((r) => headers.map((h) => JSON.stringify(r[h] ?? '')).join(','))].join('\n');
    const url  = URL.createObjectURL(new Blob([csv], { type: 'text/csv' }));
    const a    = document.createElement('a');
    a.href     = url; a.download = `campaigns-${new Date().toISOString().slice(0, 10)}.csv`;
    document.body.appendChild(a); a.click();
    document.body.removeChild(a); URL.revokeObjectURL(url);
    showToast('Exported.');
  }, [campaigns, showToast]);

  /* =========================================================================
     RENDER
     ======================================================================= */

  /* -------------------------------------------------------------------------
     Header
     ----------------------------------------------------------------------- */
  const renderHeader = () => (
    <div className="pa-header">
      <div>
        <h1>Promotion Campaigns</h1>
        <p>Manage app promotion campaigns and slides. All changes go through the upsert endpoint.</p>
      </div>
      <div className="pa-header-actions">
        <button className="btn btn-secondary" onClick={() => reloadAll(envFilter)} disabled={loading || statsLoading}>
          <RefreshIcon spinning={loading || statsLoading} /> Refresh
        </button>
        <button className="btn btn-secondary" onClick={handleExport}>
          <ExportIcon /> Export CSV
        </button>
        <button className="btn btn-primary" onClick={() => { resetForm(); setActiveTab('create'); }}>
          <PlusIcon /> New campaign
        </button>
      </div>
    </div>
  );

  /* -------------------------------------------------------------------------
     Stats bar
     ----------------------------------------------------------------------- */
  const renderStats = () => {
    const total    = campaigns.length;
    const active   = campaigns.filter((c) => c.active).length;
    const prod     = campaigns.filter((c) => c.env === 'production').length;
    const withSlides = campaigns.filter((c) => c.slides?.length).length;

    const totals = campaigns.reduce(
      (acc, c) => {
        const s = campaignStats[c.campaignId] || emptyCampaignStats();
        acc.impressions += s.impressions?.total || 0;
        acc.clicks += s.clicks?.total || 0;
        acc.dismisses += s.dismisses?.total || 0;
        return acc;
      },
      { impressions: 0, clicks: 0, dismisses: 0 },
    );
    const avgCtr = totals.impressions
      ? `${((totals.clicks / totals.impressions) * 100).toFixed(1)}%`
      : '—';

    return (
      <>
        <div className="pa-stat-row">
          {[
            { label: 'Total campaigns', value: total,      icon: <LayersIcon /> },
            { label: 'Active',          value: active,     icon: <BoltIcon />,  tone: 'success' },
            { label: 'Production',      value: prod,       icon: <EyeIcon />,   tone: 'info' },
            { label: 'Have slides',     value: withSlides, icon: <ImageIcon />, tone: 'neutral' },
          ].map((s) => (
            <div className="pa-stat-card" key={s.label}>
              <div className={cx('pa-stat-icon', `pa-stat-icon-${s.tone || 'neutral'}`)}>{s.icon}</div>
              <div>
                <div className="pa-stat-label">{s.label}</div>
                <div className="pa-stat-value">{s.value}</div>
              </div>
            </div>
          ))}
        </div>
        <div className="pa-stats-toolbar">
          <span className="pa-stats-toolbar-label">Engagement</span>
          <select
            className="select select-sm"
            value={statsDays}
            onChange={(e) => setStatsDays(e.target.value)}
          >
            <option value="all">All time</option>
            <option value="7">Last 7 days</option>
            <option value="30">Last 30 days</option>
            <option value="90">Last 90 days</option>
          </select>
        </div>
        <div className="pa-stat-row pa-stat-row-engagement">
          {[
            { label: 'Impressions', value: statsLoading ? '…' : fmtNum(totals.impressions), icon: <EyeIcon />, tone: 'info' },
            { label: 'CTA clicks',  value: statsLoading ? '…' : fmtNum(totals.clicks),      icon: <ClickIcon />, tone: 'success' },
            { label: 'Dismisses',   value: statsLoading ? '…' : fmtNum(totals.dismisses),   icon: <CloseCircleIcon />, tone: 'neutral' },
            { label: 'Avg CTR',     value: statsLoading ? '…' : avgCtr,                     icon: <ChartIcon />, tone: 'primary' },
          ].map((s) => (
            <div className="pa-stat-card" key={s.label}>
              <div className={cx('pa-stat-icon', `pa-stat-icon-${s.tone || 'neutral'}`)}>{s.icon}</div>
              <div>
                <div className="pa-stat-label">{s.label}</div>
                <div className="pa-stat-value">{s.value}</div>
              </div>
            </div>
          ))}
        </div>
      </>
    );
  };

  /* -------------------------------------------------------------------------
     Tabs
     ----------------------------------------------------------------------- */
  const renderTabs = () => (
    <div className="pa-tabs" role="tablist">
      {TABS.map((t) => (
        <button
          key={t.key} type="button" role="tab"
          className={cx('pa-tab', activeTab === t.key && 'pa-tab-active')}
          aria-selected={activeTab === t.key}
          onClick={() => setActiveTab(t.key)}
        >
          {t.label}
        </button>
      ))}
    </div>
  );

  /* -------------------------------------------------------------------------
     Campaigns tab
     ----------------------------------------------------------------------- */
  const renderCampaignsTab = () => (
    <div className="panel">
      <div className="pa-toolbar">
        <div className="pa-search">
          <SearchIcon />
          <input type="text" placeholder="Search by campaign ID…" value={search} onChange={(e) => setSearch(e.target.value)} />
        </div>
        <div className="pa-filters">
          {/* env filter also re-fetches from API */}
          <select className="select" value={envFilter} onChange={(e) => { setEnvFilter(e.target.value); setFilters((f) => ({ ...f, env: e.target.value || 'all' })); }}>
            <option value="">All environments</option>
            {ENVIRONMENTS.map((e) => <option key={e} value={e}>{e[0].toUpperCase() + e.slice(1)}</option>)}
          </select>
          <select className="select" value={filters.platform} onChange={(e) => setFilters((f) => ({ ...f, platform: e.target.value }))}>
            <option value="all">All platforms</option>
            {PLATFORMS.filter((p) => p !== 'all').map((p) => <option key={p} value={p}>{platformLabel(p)}</option>)}
          </select>
          <select className="select" value={filters.active} onChange={(e) => setFilters((f) => ({ ...f, active: e.target.value }))}>
            <option value="all">All status</option>
            <option value="true">Active</option>
            <option value="false">Inactive</option>
          </select>
          {(filters.env !== 'all' || filters.platform !== 'all' || filters.active !== 'all' || search) && (
            <button className="btn btn-ghost btn-sm" onClick={() => { setFilters(EMPTY_FILTERS); setSearch(''); setEnvFilter(''); }}>Clear</button>
          )}
        </div>
      </div>

      <div className="table-wrap">
        <table className="data-table">
          <thead>
            <tr>
              <th>Campaign ID</th>
              <th>Status</th>
              <th>Env</th>
              <th>Platform</th>
              <th>Priority</th>
              <th>Max/day</th>
              <th>Slides</th>
              <th>Impressions</th>
              <th>Clicks</th>
              <th>CTR</th>
              <th>Schedule</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {campaignsLoading ? (
              <tr><td colSpan={12} className="table-empty"><LoadingSpinner /> Loading campaigns…</td></tr>
            ) : filtered.length === 0 ? (
              <tr><td colSpan={12} className="table-empty">No campaigns match these filters.</td></tr>
            ) : filtered.map((c) => {
              const stats = campaignStats[c.campaignId] || emptyCampaignStats();
              return (
              <tr key={c.campaignId}>
                <td>
                  <button className="link-cell" onClick={() => setModal({ type: 'view', payload: c })}>
                    {c.campaignId}
                  </button>
                </td>
                <td><span className={statusCls(c.status)}>{c.status}</span></td>
                <td className="capitalize">{c.env}</td>
                <td>{platformLabel(c.platform)}</td>
                <td>{c.priority ?? 0}</td>
                <td>{c.maxImpressionsPerDay ?? 2}</td>
                <td>{c.slides?.length ?? 0}</td>
                <td className="stat-cell">{statsLoading ? '…' : fmtNum(stats.impressions?.total)}</td>
                <td className="stat-cell stat-cell-clicks">{statsLoading ? '…' : fmtNum(stats.clicks?.total)}</td>
                <td className="stat-cell">{statsLoading ? '…' : fmtCtr(stats)}</td>
                <td className="muted-text">{fmtDate(c.startAt)} → {fmtDate(c.endAt)}</td>
                <td>
                  <div className="row-actions">
                    <button className="icon-btn" title="View" onClick={() => setModal({ type: 'view', payload: c })}><EyeIcon /></button>
                    <button className="icon-btn" title="Edit" onClick={() => loadIntoForm(c)}><EditIcon /></button>
                    {c.active
                      ? <button className="icon-btn" title="Disable" onClick={() => setModal({ type: 'disable', payload: c })}><PauseIcon /></button>
                      : <button className="icon-btn" title="Enable"  onClick={() => handleToggle(c)}><PlayIcon /></button>
                    }
                  </div>
                </td>
              </tr>
            );})}
          </tbody>
        </table>
      </div>
    </div>
  );

  /* -------------------------------------------------------------------------
     Slide editor
     ----------------------------------------------------------------------- */
  const renderSlideEditor = () => (
    <div className="slides-section">
      <div className="slides-header">
        <h3>Slides</h3>
        <button type="button" className="btn btn-secondary btn-sm" onClick={addSlide}><PlusIcon /> Add slide</button>
      </div>
      {slides.map((slide, idx) => (
        <div key={slide.id} className={cx('slide-card', previewIdx === idx && 'slide-card-active')} onClick={() => setPreviewIdx(idx)}>
          <div className="slide-card-header">
            <span className="slide-index">Slide {idx + 1}</span>
            <div className="slide-card-actions">
              <button type="button" className="icon-btn icon-btn-sm" disabled={idx === 0} onClick={(e) => { e.stopPropagation(); moveSlide(slide.id, -1); }}><ArrowUpIcon /></button>
              <button type="button" className="icon-btn icon-btn-sm" disabled={idx === slides.length - 1} onClick={(e) => { e.stopPropagation(); moveSlide(slide.id, 1); }}><ArrowDownIcon /></button>
              <button type="button" className="icon-btn icon-btn-sm icon-btn-danger" disabled={slides.length === 1} onClick={(e) => { e.stopPropagation(); removeSlide(slide.id); }}><TrashIcon /></button>
            </div>
          </div>
          <div className="slide-card-body" onClick={(e) => e.stopPropagation()}>
            <div className="field slide-media-field">
              <label>Slide image</label>
              <input
                type="file"
                accept="image/jpeg,image/png,image/webp,image/gif"
                className="slide-media-input"
                ref={(el) => { slideFileInputRefs.current[slide.id] = el; }}
                onChange={(e) => {
                  const file = e.target.files?.[0];
                  if (file) handleSlideMediaUpload(slide.id, file);
                  e.target.value = '';
                }}
              />
              {slide.mediaUrl ? (
                <div className="slide-media-preview">
                  <img src={slide.mediaUrl} alt={slide.title || `Slide ${idx + 1}`} />
                  <div className="slide-media-actions">
                    <button
                      type="button"
                      className="btn btn-secondary btn-sm"
                      disabled={!!uploadingSlides[slide.id]}
                      onClick={() => triggerSlideUpload(slide.id)}
                    >
                      {uploadingSlides[slide.id] ? 'Uploading…' : 'Replace image'}
                    </button>
                    <button
                      type="button"
                      className="btn btn-ghost btn-sm"
                      disabled={!!uploadingSlides[slide.id]}
                      onClick={() => updateSlide(slide.id, 'mediaUrl', '')}
                    >
                      Remove
                    </button>
                  </div>
                </div>
              ) : (
                <button
                  type="button"
                  className="slide-media-dropzone"
                  disabled={!!uploadingSlides[slide.id]}
                  onClick={() => triggerSlideUpload(slide.id)}
                >
                  {uploadingSlides[slide.id] ? (
                    <>
                      <LoadingSpinner />
                      <span>Uploading…</span>
                    </>
                  ) : (
                    <>
                      <ImageIcon />
                      <span>Click to upload image</span>
                      <span className="slide-media-hint">JPEG, PNG, WebP, or GIF · max 10MB</span>
                    </>
                  )}
                </button>
              )}
            </div>
            <div className="field-row">
              <div className="field">
                <label>Title</label>
                <input type="text" value={slide.title} onChange={(e) => updateSlide(slide.id, 'title', e.target.value)} />
              </div>
              <div className="field">
                <label>CTA label</label>
                <input type="text" value={slide.ctaLabel} onChange={(e) => updateSlide(slide.id, 'ctaLabel', e.target.value)} />
              </div>
            </div>
            <div className="field">
              <label>Description</label>
              <textarea rows={2} value={slide.description} onChange={(e) => updateSlide(slide.id, 'description', e.target.value)} />
            </div>
            <div className="field-row">
              <div className="field">
                <label>CTA type</label>
                <select className="select" value={slide.ctaAction?.type ?? 'dismiss'}
                  onChange={(e) => updateSlideCtaAction(slide.id, 'type', e.target.value)}>
                  {CTA_TYPES.map((t) => <option key={t} value={t}>{t[0].toUpperCase() + t.slice(1)}</option>)}
                </select>
              </div>
              <div className="field">
                <label>CTA value</label>
                <input type="text"
                  disabled={slide.ctaAction?.type === 'dismiss'}
                  placeholder={slide.ctaAction?.type === 'url' ? 'https://…' : slide.ctaAction?.type === 'tab' ? 'Discover' : '—'}
                  value={slide.ctaAction?.value ?? ''}
                  onChange={(e) => updateSlideCtaAction(slide.id, 'value', e.target.value)} />
              </div>
            </div>
          </div>
        </div>
      ))}
    </div>
  );

  /* -------------------------------------------------------------------------
     Phone preview
     ----------------------------------------------------------------------- */
  const renderPreview = () => {
    const slide = slides[previewIdx] || slides[0];
    return (
      <div className="phone-preview-wrap">
        <span className="phone-preview-label">Live preview</span>
        <div className="phone-frame">
          <div className="phone-notch" />
          <div className="phone-screen">
            {slide?.mediaUrl
              ? <img src={slide.mediaUrl} alt={slide.title || 'preview'} className="phone-image" />
              : <div className="phone-image phone-image-empty"><ImageIcon /><span>Upload an image</span></div>
            }
            <div className="phone-overlay">
              <h4>{slide?.title || 'Slide title'}</h4>
              <p>{slide?.description || 'Description appears here.'}</p>
              {slide?.ctaAction?.type !== 'dismiss' && (
                <button type="button" className="phone-cta">{slide?.ctaLabel || 'CTA'}</button>
              )}
            </div>
            <div className="phone-dots">
              {slides.map((s, i) => (
                <span key={s.id} className={cx('phone-dot', i === previewIdx && 'phone-dot-active')} />
              ))}
            </div>
          </div>
        </div>
      </div>
    );
  };

  /* -------------------------------------------------------------------------
     Create / Edit tab
     ----------------------------------------------------------------------- */
  const renderCreateTab = () => (
    <form className="panel create-grid" onSubmit={handleSubmit}>
      <div className="create-main">
        <div className="form-section">
          <h3>{editingId ? `Editing — ${editingId}` : 'New campaign'}</h3>
          <div className="field-row">
            <div className="field">
              <label>Campaign ID *</label>
              <input type="text" placeholder="summer_promo_2026" value={formData.campaignId}
                disabled={!!editingId}
                onChange={(e) => setFormData((f) => ({ ...f, campaignId: e.target.value }))} required />
            </div>
            <div className="field">
              <label>Priority</label>
              <input type="number" min={0} value={formData.priority}
                onChange={(e) => setFormData((f) => ({ ...f, priority: Number(e.target.value) }))} />
            </div>
          </div>
          <div className="field-row">
            <div className="field">
              <label>Environment</label>
              <select className="select" value={formData.env} onChange={(e) => setFormData((f) => ({ ...f, env: e.target.value }))}>
                {ENVIRONMENTS.map((e) => <option key={e} value={e}>{e[0].toUpperCase() + e.slice(1)}</option>)}
              </select>
            </div>
            <div className="field">
              <label>Platform</label>
              <select className="select" value={formData.platform} onChange={(e) => setFormData((f) => ({ ...f, platform: e.target.value }))}>
                {PLATFORMS.map((p) => <option key={p} value={p}>{platformLabel(p)}</option>)}
              </select>
            </div>
          </div>
          <div className="field-row">
            <div className="field">
              <label>Max impressions / day</label>
              <input type="number" min={1} value={formData.maxImpressionsPerDay}
                onChange={(e) => setFormData((f) => ({ ...f, maxImpressionsPerDay: Number(e.target.value) }))} />
            </div>
            <div className="field field-switch">
              <label>Active</label>
              <button type="button" className={cx('switch', formData.active && 'switch-on')}
                onClick={() => setFormData((f) => ({ ...f, active: !f.active }))} aria-pressed={formData.active}>
                <span className="switch-knob" />
              </button>
            </div>
          </div>
          <div className="field-row">
            <div className="field">
              <label>Start date</label>
              <input type="date" value={formData.startAt} onChange={(e) => setFormData((f) => ({ ...f, startAt: e.target.value }))} />
            </div>
            <div className="field">
              <label>End date</label>
              <input type="date" value={formData.endAt} onChange={(e) => setFormData((f) => ({ ...f, endAt: e.target.value }))} />
            </div>
          </div>
        </div>

        {renderSlideEditor()}

        <div className="form-footer">
          {editingId && <button type="button" className="btn btn-ghost" onClick={resetForm}>Cancel edit</button>}
          <button type="submit" className="btn btn-primary" disabled={submitting}>
            {submitting ? 'Saving…' : editingId ? 'Save changes' : 'Create campaign'}
          </button>
        </div>
      </div>
      <div className="create-side">{renderPreview()}</div>
    </form>
  );

  /* -------------------------------------------------------------------------
     Modals
     ----------------------------------------------------------------------- */
  const closeModal = () => setModal({ type: null, payload: null });

  const renderModal = () => {
    const { type, payload: c } = modal;
    if (!type || !c) return null;

    if (type === 'view') {
      const stats = campaignStats[c.campaignId] || emptyCampaignStats();
      return (
      <ModalShell title={c.campaignId} onClose={closeModal}>
        <div className="view-stats-grid">
          {[
            ['Impressions', fmtNum(stats.impressions?.total), `${fmtNum(stats.impressions?.uniqueUsers)} users`],
            ['CTA clicks', fmtNum(stats.clicks?.total), `${fmtNum(stats.clicks?.uniqueUsers)} users`],
            ['Dismisses', fmtNum(stats.dismisses?.total), `${fmtNum(stats.dismisses?.uniqueUsers)} users`],
            ['CTR', fmtCtr(stats), 'clicks / impressions'],
          ].map(([label, value, sub]) => (
            <div className="view-stat-card" key={label}>
              <span className="view-stat-label">{label}</span>
              <strong className="view-stat-value">{statsLoading ? '…' : value}</strong>
              <span className="view-stat-sub">{statsLoading ? '' : sub}</span>
            </div>
          ))}
        </div>

        <div className="view-platform-stats">
          <h4>By platform</h4>
          <div className="view-platform-row">
            <span>iOS</span>
            <span>{fmtNum(stats.byPlatform?.ios?.impressions)} imp</span>
            <span>{fmtNum(stats.byPlatform?.ios?.clicks)} clicks</span>
            <span>{fmtNum(stats.byPlatform?.ios?.dismisses)} dismiss</span>
          </div>
          <div className="view-platform-row">
            <span>Android</span>
            <span>{fmtNum(stats.byPlatform?.android?.impressions)} imp</span>
            <span>{fmtNum(stats.byPlatform?.android?.clicks)} clicks</span>
            <span>{fmtNum(stats.byPlatform?.android?.dismisses)} dismiss</span>
          </div>
        </div>

        <dl className="detail-list">
          {[
            ['Status',        <span className={statusCls(c.status)}>{c.status}</span>],
            ['Environment',   c.env],
            ['Platform',      platformLabel(c.platform)],
            ['Priority',      c.priority ?? 0],
            ['Max/day',       c.maxImpressionsPerDay ?? 2],
            ['Active',        c.active ? 'Yes' : 'No'],
            ['Schedule',      `${fmtDate(c.startAt)} → ${fmtDate(c.endAt)}`],
            ['Slides',        c.slides?.length ?? 0],
          ].map(([k, v]) => (
            <div key={k}><dt>{k}</dt><dd>{v}</dd></div>
          ))}
        </dl>
        {c.slides?.length > 0 && (
          <div className="view-slides">
            <h4>Slides ({c.slides.length})</h4>
            {c.slides.map((s, i) => {
              const slideStats = stats.bySlide?.[s.id] || stats.bySlide?.unknown || {};
              return (
              <div className="view-slide-row" key={s.id || i}>
                {s.mediaUrl && <img src={s.mediaUrl} alt={s.title} />}
                <div>
                  <strong>{s.title}</strong>
                  <p>{s.description}</p>
                  <span className="muted-text">CTA: {s.ctaLabel} ({s.ctaAction?.type}{s.ctaAction?.type !== 'dismiss' ? ` → ${s.ctaAction?.value}` : ''})</span>
                  <span className="slide-stats-line">
                    {fmtNum(slideStats.impressions)} impressions · {fmtNum(slideStats.clicks)} clicks · {fmtNum(slideStats.dismisses)} dismisses
                  </span>
                </div>
              </div>
            );})}
          </div>
        )}
        <div className="modal-footer">
          <button className="btn btn-secondary" onClick={() => { closeModal(); loadIntoForm(c); }}>Edit</button>
          <button className="btn btn-primary" onClick={closeModal}>Close</button>
        </div>
      </ModalShell>
    );}

    if (type === 'disable') return (
      <ModalShell title="Disable campaign" onClose={closeModal} tone="danger">
        <p>Disabling <strong>{c.campaignId}</strong> will stop it from showing to users immediately. You can re-enable it any time.</p>
        <div className="modal-footer">
          <button className="btn btn-ghost" onClick={closeModal}>Cancel</button>
          <button className="btn btn-danger" onClick={() => handleToggle(c)}>Disable</button>
        </div>
      </ModalShell>
    );

    return null;
  };

  /* -------------------------------------------------------------------------
     Main
     ----------------------------------------------------------------------- */
  return (
    <div className="pa-page">
      {renderHeader()}
      {renderStats()}
      {renderTabs()}
      <div className="pa-tab-content">
        {activeTab === 'campaigns' && renderCampaignsTab()}
        {activeTab === 'create'    && renderCreateTab()}
      </div>
      {renderModal()}
      {toast && (
        <div className={cx('toast', toast.kind === 'error' && 'toast-error')}>
          {toast.kind === 'error' ? <CloseCircleIcon /> : <CheckCircleIcon />}
          <span>{toast.message}</span>
        </div>
      )}
      <style>{STYLES}</style>
    </div>
  );
}

/* ============================================================================
   Shared small components
   ========================================================================== */
function ModalShell({ title, tone, onClose, children }) {
  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className={cx('modal-content', tone === 'danger' && 'modal-content-danger')}
        onClick={(e) => e.stopPropagation()} role="dialog" aria-modal="true">
        <div className="modal-header">
          <h3>{title}</h3>
          <button type="button" className="icon-btn" onClick={onClose} aria-label="Close"><CloseIcon /></button>
        </div>
        <div className="modal-body">{children}</div>
      </div>
    </div>
  );
}

function LoadingSpinner() { return <span className="spinner" aria-hidden="true" />; }

/* ---------------------------------------------------------------------------
   Icons
   ------------------------------------------------------------------------- */
const IP = { width: 16, height: 16, viewBox: '0 0 24 24', fill: 'none', stroke: 'currentColor', strokeWidth: 2, strokeLinecap: 'round', strokeLinejoin: 'round' };
const PlusIcon         = () => <svg {...IP}><path d="M12 5v14M5 12h14"/></svg>;
const RefreshIcon      = ({ spinning }) => <svg {...IP} className={spinning ? 'spin' : undefined}><path d="M21 12a9 9 0 1 1-2.64-6.36M21 4v6h-6"/></svg>;
const ExportIcon       = () => <svg {...IP}><path d="M12 3v12m0-12 4 4m-4-4-4 4M5 17v2a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2v-2"/></svg>;
const SearchIcon       = () => <svg {...IP}><circle cx="11" cy="11" r="7"/><path d="M21 21l-4.3-4.3"/></svg>;
const EyeIcon          = () => <svg {...IP}><path d="M1 12s4-7 11-7 11 7 11 7-4 7-11 7-11-7-11-7Z"/><circle cx="12" cy="12" r="3"/></svg>;
const EditIcon         = () => <svg {...IP}><path d="M12 20h9"/><path d="M16.5 3.5a2.1 2.1 0 0 1 3 3L7 19l-4 1 1-4Z"/></svg>;
const PauseIcon        = () => <svg {...IP}><rect x="6" y="4" width="4" height="16" rx="1"/><rect x="14" y="4" width="4" height="16" rx="1"/></svg>;
const PlayIcon         = () => <svg {...IP}><path d="M6 3v18l15-9Z"/></svg>;
const TrashIcon        = () => <svg {...IP}><path d="M3 6h18M8 6V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2m3 0-1 14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2L4 6"/></svg>;
const CloseIcon        = () => <svg {...IP}><path d="M18 6 6 18M6 6l12 12"/></svg>;
const ArrowUpIcon      = () => <svg {...IP}><path d="M12 19V5M5 12l7-7 7 7"/></svg>;
const ArrowDownIcon    = () => <svg {...IP}><path d="M12 5v14M19 12l-7 7-7-7"/></svg>;
const ImageIcon        = () => <svg {...IP}><rect x="3" y="3" width="18" height="18" rx="2"/><circle cx="8.5" cy="8.5" r="1.5"/><path d="M21 15l-5-5L5 21"/></svg>;
const BoltIcon         = () => <svg {...IP}><path d="M13 2 3 14h7l-1 8 10-12h-7l1-8Z"/></svg>;
const ClickIcon        = () => <svg {...IP}><path d="M9 9V5.5a2.5 2.5 0 0 1 5 0V9"/><path d="M8 9h8l-1 10H9L8 9Z"/></svg>;
const ChartIcon        = () => <svg {...IP}><path d="M3 3v18h18"/><path d="M7 16V9"/><path d="M12 16V5"/><path d="M17 16v-4"/></svg>;
const LayersIcon       = () => <svg {...IP}><path d="m12 2 9 5-9 5-9-5 9-5Z"/><path d="m3 12 9 5 9-5"/><path d="m3 17 9 5 9-5"/></svg>;
const CloseCircleIcon  = () => <svg {...IP}><circle cx="12" cy="12" r="9"/><path d="M15 9l-6 6M9 9l6 6"/></svg>;
const CheckCircleIcon  = () => <svg {...IP}><circle cx="12" cy="12" r="9"/><path d="M8.5 12.5l2.5 2.5 5-5.5"/></svg>;

/* ============================================================================
   Styles
   ========================================================================== */
const STYLES = `
.pa-page {
  --pa-primary:#5b5fef; --pa-primary-dark:#4338ca; --pa-primary-soft:#eef0ff;
  --pa-success:#12b76a; --pa-success-soft:#ecfdf3;
  --pa-info:#2e90fa;    --pa-info-soft:#eff8ff;
  --pa-warning:#f79009; --pa-warning-soft:#fffaeb;
  --pa-danger:#f04438;  --pa-danger-soft:#fef3f2;
  --pa-neutral-soft:#f2f4f7;
  --pa-text:#101828; --pa-text-2:#475467; --pa-text-3:#98a2b3;
  --pa-bg:#f7f8fb; --pa-surface:#ffffff;
  --pa-border:#e4e7ec; --pa-border-strong:#d0d5dd;
  --pa-radius-lg:14px; --pa-radius-md:10px; --pa-radius-sm:7px;
  --pa-shadow-sm:0 1px 2px rgba(16,24,40,.06);
  --pa-shadow-md:0 4px 16px rgba(16,24,40,.08);
  font-family:'Inter',-apple-system,BlinkMacSystemFont,'Segoe UI',Roboto,sans-serif;
  background:var(--pa-bg); color:var(--pa-text);
  padding:28px 32px 64px; min-height:100%; box-sizing:border-box; font-size:14px; line-height:1.45;
}
.pa-page * { box-sizing:border-box; }
.pa-page h1,.pa-page h2,.pa-page h3,.pa-page h4 { margin:0; font-weight:600; letter-spacing:-.01em; }
.pa-page p { margin:0; }
.pa-page table { border-collapse:collapse; }
.pa-page input,.pa-page select,.pa-page textarea,.pa-page button { font-family:inherit; font-size:13.5px; color:var(--pa-text); }
.muted-text { color:var(--pa-text-2); }
.capitalize { text-transform:capitalize; }

/* Header */
.pa-header { display:flex; align-items:flex-start; justify-content:space-between; gap:24px; margin-bottom:24px; flex-wrap:wrap; }
.pa-header h1 { font-size:24px; }
.pa-header p  { margin-top:6px; color:var(--pa-text-2); }
.pa-header-actions { display:flex; gap:10px; flex-wrap:wrap; }

/* Stat row */
.pa-stat-row { display:grid; grid-template-columns:repeat(4,1fr); gap:14px; margin-bottom:24px; }
.pa-stat-card { background:var(--pa-surface); border:1px solid var(--pa-border); border-radius:var(--pa-radius-lg); padding:16px; display:flex; align-items:center; gap:12px; box-shadow:var(--pa-shadow-sm); }
.pa-stat-icon { width:34px; height:34px; border-radius:9px; display:flex; align-items:center; justify-content:center; flex-shrink:0; }
.pa-stat-icon-success { background:var(--pa-success-soft); color:var(--pa-success); }
.pa-stat-icon-info    { background:var(--pa-info-soft);    color:var(--pa-info); }
.pa-stat-icon-primary { background:var(--pa-primary-soft); color:var(--pa-primary); }
.pa-stat-icon-neutral { background:var(--pa-neutral-soft); color:var(--pa-text-2); }
.pa-stat-row-engagement { margin-bottom:24px; }
.pa-stats-toolbar { display:flex; align-items:center; justify-content:space-between; gap:12px; margin:4px 0 12px; }
.pa-stats-toolbar-label { font-size:12px; font-weight:600; text-transform:uppercase; letter-spacing:.04em; color:var(--pa-text-3); }
.select-sm { padding:6px 10px; font-size:12.5px; }
.stat-cell { font-variant-numeric:tabular-nums; font-weight:600; }
.stat-cell-clicks { color:#067647; }
.pa-stat-label { font-size:12px; color:var(--pa-text-2); }
.pa-stat-value { font-size:20px; font-weight:700; letter-spacing:-.01em; }

/* Tabs */
.pa-tabs { display:flex; border-bottom:1px solid var(--pa-border); margin-bottom:20px; gap:4px; overflow-x:auto; }
.pa-tab  { background:transparent; border:none; padding:10px 4px; margin-right:22px; font-weight:500; color:var(--pa-text-2); cursor:pointer; position:relative; white-space:nowrap; }
.pa-tab:hover { color:var(--pa-text); }
.pa-tab-active { color:var(--pa-primary); }
.pa-tab-active::after { content:''; position:absolute; left:0; right:0; bottom:-1px; height:2px; background:var(--pa-primary); border-radius:2px 2px 0 0; }

/* Panel */
.panel { background:var(--pa-surface); border:1px solid var(--pa-border); border-radius:var(--pa-radius-lg); box-shadow:var(--pa-shadow-sm); padding:20px; }

/* Toolbar */
.pa-toolbar { display:flex; align-items:center; justify-content:space-between; gap:16px; flex-wrap:wrap; margin-bottom:16px; }
.pa-search  { display:flex; align-items:center; gap:8px; border:1px solid var(--pa-border); border-radius:var(--pa-radius-sm); padding:8px 12px; background:var(--pa-surface); min-width:240px; color:var(--pa-text-3); }
.pa-search input { border:none; outline:none; flex:1; color:var(--pa-text); background:transparent; }
.pa-filters { display:flex; gap:8px; flex-wrap:wrap; align-items:center; }

/* Buttons */
.btn { display:inline-flex; align-items:center; gap:6px; padding:9px 14px; border-radius:var(--pa-radius-sm); border:1px solid transparent; font-weight:500; cursor:pointer; transition:background .15s,border-color .15s,transform .05s,box-shadow .15s; white-space:nowrap; }
.btn:active { transform:translateY(1px); }
.btn:disabled { opacity:.55; cursor:not-allowed; }
.btn-primary   { background:linear-gradient(135deg,var(--pa-primary),var(--pa-primary-dark)); color:#fff; box-shadow:var(--pa-shadow-sm); }
.btn-primary:hover:not(:disabled) { box-shadow:0 6px 16px rgba(91,95,239,.32); }
.btn-secondary { background:var(--pa-surface); border-color:var(--pa-border); color:var(--pa-text); }
.btn-secondary:hover:not(:disabled) { border-color:var(--pa-border-strong); background:#fafbfc; }
.btn-ghost     { background:transparent; color:var(--pa-text-2); }
.btn-ghost:hover:not(:disabled) { background:var(--pa-neutral-soft); color:var(--pa-text); }
.btn-danger    { background:var(--pa-danger); color:#fff; }
.btn-danger:hover:not(:disabled) { background:#d92d20; }
.btn-sm { padding:6px 10px; font-size:12.5px; }

/* Icon buttons */
.icon-btn { display:inline-flex; align-items:center; justify-content:center; width:30px; height:30px; border-radius:var(--pa-radius-sm); border:1px solid transparent; background:transparent; color:var(--pa-text-2); cursor:pointer; transition:background .15s,color .15s; }
.icon-btn:hover:not(:disabled) { background:var(--pa-neutral-soft); color:var(--pa-text); }
.icon-btn:disabled { opacity:.4; cursor:not-allowed; }
.icon-btn-danger:hover:not(:disabled) { background:var(--pa-danger-soft); color:var(--pa-danger); }
.icon-btn-sm { width:26px; height:26px; }

/* Select */
.select { border:1px solid var(--pa-border); border-radius:var(--pa-radius-sm); padding:8px 10px; background:var(--pa-surface); color:var(--pa-text); cursor:pointer; }

/* Table */
.table-wrap { overflow-x:auto; border:1px solid var(--pa-border); border-radius:var(--pa-radius-md); }
.data-table { width:100%; min-width:800px; }
.data-table thead th { text-align:left; font-size:12px; text-transform:uppercase; letter-spacing:.03em; color:var(--pa-text-3); background:var(--pa-neutral-soft); padding:10px 14px; border-bottom:1px solid var(--pa-border); white-space:nowrap; }
.data-table tbody td { padding:12px 14px; border-bottom:1px solid var(--pa-border); white-space:nowrap; font-size:13.5px; }
.data-table tbody tr:last-child td { border-bottom:none; }
.data-table tbody tr:hover { background:#fafbfd; }
.table-empty { text-align:center; padding:32px; color:var(--pa-text-2); }
.link-cell { background:none; border:none; padding:0; color:var(--pa-text); font-weight:550; cursor:pointer; }
.link-cell:hover { color:var(--pa-primary); text-decoration:underline; }
.row-actions { display:flex; gap:2px; }

/* Badges */
.badge { display:inline-flex; align-items:center; text-transform:capitalize; padding:3px 9px; border-radius:999px; font-size:12px; font-weight:550; }
.badge-success { background:var(--pa-success-soft); color:#067647; }
.badge-info    { background:var(--pa-info-soft);    color:#175cd3; }
.badge-warning { background:var(--pa-warning-soft); color:#93530c; }
.badge-danger  { background:var(--pa-danger-soft);  color:#b42318; }
.badge-neutral { background:var(--pa-neutral-soft); color:var(--pa-text-2); }

/* Spin */
.spin { animation:pa-spin .9s linear infinite; }
@keyframes pa-spin { to { transform:rotate(360deg); } }
.spinner { width:14px; height:14px; border:2px solid var(--pa-border); border-top-color:var(--pa-primary); border-radius:50%; display:inline-block; animation:pa-spin .7s linear infinite; margin-right:6px; vertical-align:middle; }

/* Create layout */
.create-grid { display:grid; grid-template-columns:1fr 320px; gap:24px; align-items:start; }
.create-side  { position:sticky; top:16px; display:flex; justify-content:center; }
.create-main  { display:flex; flex-direction:column; gap:0; }

/* Form */
.form-section { border:1px solid var(--pa-border); border-radius:var(--pa-radius-md); padding:18px; margin-bottom:18px; }
.form-section h3 { font-size:14.5px; margin-bottom:14px; }
.field-row { display:grid; grid-template-columns:1fr 1fr; gap:14px; margin-bottom:14px; }
.field { display:flex; flex-direction:column; gap:6px; }
.field label { font-size:12.5px; font-weight:550; color:var(--pa-text-2); }
.field input,.field select,.field textarea { border:1px solid var(--pa-border); border-radius:var(--pa-radius-sm); padding:8px 10px; background:var(--pa-surface); width:100%; resize:vertical; }
.field input:disabled { background:var(--pa-neutral-soft); color:var(--pa-text-3); }
.field-switch { flex-direction:row; align-items:center; justify-content:space-between; padding-top:18px; }
.switch { width:40px; height:22px; border-radius:999px; background:var(--pa-border-strong,#d0d5dd); border:none; position:relative; cursor:pointer; flex-shrink:0; transition:background .15s; }
.switch-on { background:var(--pa-primary); }
.switch-knob { position:absolute; top:3px; left:3px; width:16px; height:16px; background:#fff; border-radius:50%; transition:transform .15s; box-shadow:0 1px 2px rgba(16,24,40,.25); }
.switch-on .switch-knob { transform:translateX(18px); }
.form-footer { display:flex; justify-content:flex-end; gap:10px; margin-top:6px; }

/* Slides */
.slides-section { border:1px solid var(--pa-border); border-radius:var(--pa-radius-md); padding:18px; margin-bottom:18px; }
.slides-header  { display:flex; align-items:center; justify-content:space-between; margin-bottom:14px; }
.slides-header h3 { font-size:14.5px; }
.slide-card { border:1px solid var(--pa-border); border-radius:var(--pa-radius-md); padding:12px 14px; margin-bottom:12px; cursor:pointer; transition:border-color .15s,box-shadow .15s; }
.slide-card:last-child { margin-bottom:0; }
.slide-card-active { border-color:var(--pa-primary); box-shadow:0 0 0 3px var(--pa-primary-soft); }
.slide-card-header { display:flex; align-items:center; justify-content:space-between; margin-bottom:10px; }
.slide-index { font-size:12.5px; font-weight:600; color:var(--pa-text-2); }
.slide-card-actions { display:flex; gap:2px; }
.slide-card-body { display:flex; flex-direction:column; gap:10px; }
.slide-media-field { gap:8px; }
.slide-media-input { display:none; }
.slide-media-dropzone {
  width:100%; min-height:132px; border:1px dashed var(--pa-border-strong);
  border-radius:var(--pa-radius-md); background:#fafbfc; color:var(--pa-text-2);
  display:flex; flex-direction:column; align-items:center; justify-content:center; gap:8px;
  padding:18px; cursor:pointer; transition:border-color .15s,background .15s;
}
.slide-media-dropzone:hover:not(:disabled) { border-color:var(--pa-primary); background:var(--pa-primary-soft); color:var(--pa-text); }
.slide-media-dropzone:disabled { opacity:.7; cursor:not-allowed; }
.slide-media-hint { font-size:12px; color:var(--pa-text-3); }
.slide-media-preview { display:flex; flex-direction:column; gap:10px; }
.slide-media-preview img {
  width:100%; max-height:180px; object-fit:cover; border-radius:var(--pa-radius-md);
  border:1px solid var(--pa-border); background:var(--pa-neutral-soft);
}
.slide-media-actions { display:flex; gap:8px; flex-wrap:wrap; }

/* Phone preview */
.phone-preview-wrap { display:flex; flex-direction:column; align-items:center; gap:12px; }
.phone-preview-label { font-size:12px; font-weight:600; text-transform:uppercase; letter-spacing:.06em; color:var(--pa-text-3); }
.phone-frame { width:248px; height:504px; background:#0b0d12; border-radius:38px; padding:10px; box-shadow:0 20px 40px rgba(16,24,40,.22),0 2px 6px rgba(16,24,40,.18); position:relative; }
.phone-notch { position:absolute; top:10px; left:50%; transform:translateX(-50%); width:92px; height:18px; background:#0b0d12; border-radius:0 0 14px 14px; z-index:2; }
.phone-screen { width:100%; height:100%; border-radius:28px; overflow:hidden; position:relative; background:#f2f4f7; }
.phone-image { width:100%; height:100%; object-fit:cover; display:block; }
.phone-image-empty { display:flex; flex-direction:column; align-items:center; justify-content:center; gap:8px; color:var(--pa-text-3); font-size:12px; height:100%; }
.phone-overlay { position:absolute; left:0; right:0; bottom:0; padding:18px 16px 26px; background:linear-gradient(180deg,rgba(10,12,18,0) 0%,rgba(10,12,18,.78) 55%,rgba(10,12,18,.92) 100%); color:#fff; }
.phone-overlay h4 { font-size:16px; margin-bottom:4px; }
.phone-overlay p  { font-size:12px; opacity:.85; margin-bottom:12px; line-height:1.4; }
.phone-cta { background:#fff; color:#101828; border:none; border-radius:999px; padding:8px 16px; font-weight:600; font-size:12.5px; cursor:pointer; }
.phone-dots { position:absolute; top:32px; left:0; right:0; display:flex; justify-content:center; gap:5px; }
.phone-dot { width:5px; height:5px; border-radius:50%; background:rgba(255,255,255,.4); }
.phone-dot-active { background:#fff; width:14px; border-radius:3px; }

/* Modal */
.modal-overlay { position:fixed; inset:0; background:rgba(16,24,40,.55); display:flex; align-items:center; justify-content:center; padding:24px; z-index:1000; }
.modal-content { background:var(--pa-surface); border-radius:var(--pa-radius-lg); width:100%; max-width:560px; max-height:86vh; overflow-y:auto; box-shadow:0 24px 48px rgba(16,24,40,.28); }
.modal-header { display:flex; align-items:center; justify-content:space-between; padding:18px 20px; border-bottom:1px solid var(--pa-border); position:sticky; top:0; background:var(--pa-surface); }
.modal-header h3 { font-size:15.5px; }
.modal-content-danger .modal-header h3 { color:var(--pa-danger); }
.modal-body { padding:20px; font-size:13.5px; color:var(--pa-text-2); }
.modal-footer { display:flex; justify-content:flex-end; gap:10px; margin-top:18px; }
.detail-list { display:grid; grid-template-columns:1fr 1fr; gap:10px 16px; margin:0; }
.detail-list dt { font-size:11.5px; color:var(--pa-text-3); text-transform:uppercase; letter-spacing:.03em; }
.detail-list dd { margin:2px 0 0; font-weight:550; color:var(--pa-text); }
.view-slides h4 { font-size:13px; margin:14px 0 10px; }
.view-slide-row { display:flex; gap:12px; padding:10px 0; border-top:1px solid var(--pa-border); }
.view-slide-row img { width:56px; height:72px; object-fit:cover; border-radius:8px; flex-shrink:0; }
.view-slide-row strong { color:var(--pa-text); font-size:13px; }
.view-slide-row p { font-size:12.5px; margin:3px 0 5px; }
.slide-stats-line { display:block; font-size:11.5px; color:var(--pa-text-3); margin-top:6px; }
.view-stats-grid { display:grid; grid-template-columns:repeat(2,1fr); gap:10px; margin-bottom:16px; }
.view-stat-card { background:var(--pa-neutral-soft); border-radius:var(--pa-radius-md); padding:12px 14px; }
.view-stat-label { display:block; font-size:11px; text-transform:uppercase; letter-spacing:.03em; color:var(--pa-text-3); margin-bottom:4px; }
.view-stat-value { display:block; font-size:20px; color:var(--pa-text); margin-bottom:2px; }
.view-stat-sub { font-size:11.5px; color:var(--pa-text-3); }
.view-platform-stats { margin-bottom:16px; }
.view-platform-stats h4 { font-size:13px; margin-bottom:8px; }
.view-platform-row { display:grid; grid-template-columns:80px repeat(3,1fr); gap:8px; font-size:12px; padding:6px 0; border-top:1px solid var(--pa-border); color:var(--pa-text-2); }
.view-platform-row span:first-child { font-weight:600; color:var(--pa-text); }

/* Toast */
.toast { position:fixed; bottom:24px; right:24px; background:var(--pa-text); color:#fff; padding:12px 16px; border-radius:var(--pa-radius-md); display:flex; align-items:center; gap:8px; box-shadow:var(--pa-shadow-md); font-size:13px; z-index:1100; animation:pa-toast-in .18s ease; }
.toast-error { background:var(--pa-danger); }
@keyframes pa-toast-in { from { opacity:0; transform:translateY(8px); } to { opacity:1; transform:translateY(0); } }

/* Responsive */
@media(max-width:1080px) { .create-grid { grid-template-columns:1fr; } .create-side { position:static; order:-1; } }
@media(max-width:760px)  { .pa-page { padding:18px 16px 48px; } .pa-stat-row { grid-template-columns:repeat(2,1fr); } .field-row { grid-template-columns:1fr; } }
@media(max-width:480px)  { .pa-stat-row { grid-template-columns:1fr; } }
`;
